import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { countBusinessDays, getEditDeadline, canEditOrder, canRequestChange } from '../../lib/orderEditPolicy';
import { 
  Package, 
  Search, 
  Filter, 
  ArrowLeft, 
  Calendar, 
  Clock, 
  MapPin, 
  FileText,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Truck,
  ChefHat,
  XCircle,
  Edit2,
  Save,
  X,
  Plus,
  Minus,
  Trash2,
  Info,
  Camera
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Calendar as CalendarComponent } from '../../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover';
import { WeekView } from '../../components/WeekView';
import { clientPortalService } from '../../services/clientPortal.service';
import { RequestChangeDialog } from './RequestChangeDialog';
import { PhotoProofGallery } from '../../components/verification/PhotoProofGallery';
import { OverCapNotice } from '../../components/insights/OverCapNotice';
import { toast } from 'sonner';
import { useCart } from '../../contexts/CartContext';
import { format, addDays, startOfDay } from 'date-fns';

const statusConfig = {
  submitted: { label: 'Submitted', color: 'bg-yellow-100 text-yellow-800', icon: FileText },
  pending_approval: { label: 'Pending Approval', color: 'bg-amber-100 text-amber-800', icon: Clock },
  confirmed: { label: 'Confirmed', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
  in_production: { label: 'In Production', color: 'bg-purple-100 text-purple-800', icon: ChefHat },
  ready_for_delivery: { label: 'Ready for Delivery', color: 'bg-indigo-100 text-indigo-800', icon: Truck },
  out_for_delivery: { label: 'Out for Delivery', color: 'bg-indigo-100 text-indigo-800', icon: Truck },
  delivered: { label: 'Delivered', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  failed_delivery: { label: 'Failed Delivery', color: 'bg-orange-100 text-orange-800', icon: AlertCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-800', icon: AlertCircle },
};

// Parse date safely — date-only strings are UTC midnight in JS, shifting to previous day in western TZs
const safeDate = (d) => {
  if (!d) return null;
  const s = typeof d === 'string' ? d : String(d);
  return new Date(s.length === 10 ? s + 'T12:00:00' : s);
};

const OrdersList = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [allowedDeliveryDays, setAllowedDeliveryDays] = useState(null);
  const [weekFilteredOrders, setWeekFilteredOrders] = useState(null);
  const [weekScopeOrders, setWeekScopeOrders] = useState(null);

  useEffect(() => {
    loadOrders();
    loadProfile();
  }, []);

  const loadOrders = async () => {
    try {
      const data = await clientPortalService.getOrders();
      setOrders(data);
    } catch (error) {
      console.error('Error loading orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const loadProfile = async () => {
    try {
      const profile = await clientPortalService.getProfile();
      setAllowedDeliveryDays(profile?.account?.preferences?.allowed_delivery_days || []);
    } catch (error) {
      console.error('Error loading profile:', error);
    }
  };

  const handleWeekFilterChange = (filtered, everyStatus) => {
    setWeekFilteredOrders(filtered);
    setWeekScopeOrders(filtered === null ? null : (everyStatus ?? filtered));
  };

  const baseOrders = weekFilteredOrders !== null ? weekFilteredOrders : orders;
  // The strip hands over live work only — a cancelled order or a failed
  // delivery is not what the week holds. The three tabs below are a census of
  // the same week rather than a total, so they read the fuller list: otherwise
  // the Cancelled tab would read zero on a week view and show nothing.
  const scopeOrders = weekScopeOrders !== null ? weekScopeOrders : orders;

  const matchesFilters = (order) => {
    const matchesSearch = (order.order_number || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  };

  const filteredOrders = baseOrders.filter(matchesFilters);
  const censusOrders = scopeOrders.filter(matchesFilters);

  // Group orders by status type
  const activeOrders = censusOrders.filter(o =>
    ['submitted', 'pending_approval', 'confirmed', 'in_production', 'ready_for_delivery', 'out_for_delivery', 'failed_delivery'].includes(o.status)
  );
  const completedOrders = filteredOrders.filter(o => o.status === 'delivered');
  const cancelledOrders = censusOrders.filter(o => o.status === 'cancelled');

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="orders-loading">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-amber-500"></div>
          <p className="mt-4 text-gray-600">Loading orders...</p>
        </div>
      </div>
    );
  }

  const OrderCard = ({ order }) => {
    const status = statusConfig[order.status] || statusConfig.submitted;
    const StatusIcon = status.icon;
    
    return (
      <Link to={`/client/orders/${order.id}`} data-testid={`order-${order.order_number}`}>
        <Card className="hover:shadow-md transition-shadow cursor-pointer">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-lg ${status.color.split(' ')[0]}`}>
                  <StatusIcon className={`h-5 w-5 ${status.color.split(' ')[1]}`} />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">
                    {order.order_number}
                    {order.billing_timing === 'advance' && (
                      <span
                        className="ml-2 text-xs bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded-full font-normal align-middle"
                        data-testid={`prepaid-chip-${order.order_number}`}
                      >
                        Invoiced in advance
                      </span>
                    )}
                    {order.has_pending_change_request && (
                      <span
                        className="ml-2 text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full font-normal align-middle"
                        data-testid={`change-pending-chip-${order.order_number}`}
                      >
                        Change pending
                      </span>
                    )}
                  </p>
                  <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                    <Calendar className="h-3 w-3" />
                    <span>
                      {safeDate(order.delivery_date)?.toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </span>
                    <Clock className="h-3 w-3 ml-2" />
                    <span>{order.delivery_time_window}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4 sm:text-right">
                <Badge className={status.color}>{status.label}</Badge>
                <div>
                  <p className="font-bold text-amber-600">${order.total_amount.toFixed(2)}</p>
                  {order.total_savings > 0 && (
                    <p className="text-xs text-green-600">Saved ${order.total_savings.toFixed(2)}</p>
                  )}
                </div>
              </div>
            </div>
            {order.items && (
              <div className="mt-3 pt-3 border-t">
                <p className="text-sm text-gray-600">
                  {order.items.length} item{order.items.length !== 1 ? 's' : ''}: {' '}
                  {order.items.slice(0, 2).map(i => i.product_name).join(', ')}
                  {order.items.length > 2 && ` +${order.items.length - 2} more`}
                </p>
              </div>
            )}
            {order.over_cap && (
              <div className="mt-3">
                <OverCapNotice order={order} compact />
              </div>
            )}
          </CardContent>
        </Card>
      </Link>
    );
  };

  return (
    <div className="space-y-6" data-testid="orders-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Orders</h1>
        <p className="text-sm text-gray-600 mt-1">View and track your orders</p>
      </div>

      {/* Week View */}
      <WeekView
        orders={orders}
        allowedDeliveryDays={allowedDeliveryDays}
        onFilterChange={handleWeekFilterChange}
      />

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search by order number..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-orders"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48" data-testid="status-filter">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {Object.entries(statusConfig).map(([key, config]) => (
              <SelectItem key={key} value={key}>{config.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Orders Tabs */}
      <Tabs defaultValue="active" className="space-y-4">
        <TabsList>
          <TabsTrigger value="active" data-testid="tab-active">
            Active ({activeOrders.length})
          </TabsTrigger>
          <TabsTrigger value="completed" data-testid="tab-completed">
            Completed ({completedOrders.length})
          </TabsTrigger>
          <TabsTrigger value="cancelled" data-testid="tab-cancelled">
            Cancelled ({cancelledOrders.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="space-y-4">
          {activeOrders.length === 0 ? (
            <div className="text-center py-12">
              <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No active orders</p>
              <Link to="/client/shop">
                <Button className="mt-4 bg-amber-500 hover:bg-amber-600">Start Shopping</Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {activeOrders.map(order => (
                <OrderCard key={order.id} order={order} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="completed" className="space-y-4">
          {completedOrders.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No completed orders yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {completedOrders.map(order => (
                <OrderCard key={order.id} order={order} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="cancelled" className="space-y-4">
          {cancelledOrders.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No cancelled orders</p>
            </div>
          ) : (
            <div className="space-y-3">
              {cancelledOrders.map(order => (
                <OrderCard key={order.id} order={order} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

const timeWindows = [
  '6:00 AM - 8:00 AM',
  '7:00 AM - 9:00 AM',
  '8:00 AM - 10:00 AM',
  '9:00 AM - 11:00 AM',
  '10:00 AM - 12:00 PM',
];

const dayNameMap = { 0: 'sunday', 1: 'monday', 2: 'tuesday', 3: 'wednesday', 4: 'thursday', 5: 'friday', 6: 'saturday' };

const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reordering, setReordering] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [changeOpen, setChangeOpen] = useState(false);
  const { addToCart, clearCart } = useCart();

  // Edit state
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState([]);
  const [profile, setProfile] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [productSearch, setProductSearch] = useState('');

  const loadOrder = useCallback(async () => {
    try {
      const orders = await clientPortalService.getOrders();
      const foundOrder = orders.find(o => o.id === id);
      if (foundOrder) {
        setOrder(foundOrder);
      } else {
        toast.error('Order not found');
        navigate('/client/orders');
      }
    } catch (error) {
      console.error('Error loading order:', error);
      toast.error('Failed to load order');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  const loadEditData = async () => {
    try {
      const [productsData, profileData] = await Promise.all([
        clientPortalService.getProducts(),
        clientPortalService.getProfile()
      ]);
      setProducts(productsData);
      setProfile(profileData);
    } catch (error) {
      console.error('Error loading edit data:', error);
      toast.error('Failed to load editing data');
    }
  };

  const handleStartEdit = async () => {
    await loadEditData();
    setEditForm({
      items: order.items.map(item => ({ ...item })),
      delivery_date: safeDate(order.delivery_date),
      delivery_time_window: order.delivery_time_window || '7:00 AM - 9:00 AM',
      location_id: order.location_id || '',
      delivery_address: order.delivery_address || '',
      po_number: order.po_number || '',
      order_notes: order.order_notes || '',
    });
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditForm(null);
    setAddItemOpen(false);
  };

  const handleUpdateQuantity = (index, delta) => {
    setEditForm(prev => {
      const newItems = [...prev.items];
      const newQty = Math.max(1, (newItems[index].quantity || 1) + delta);
      newItems[index] = {
        ...newItems[index],
        quantity: newQty,
        subtotal: newQty * newItems[index].unit_price
      };
      return { ...prev, items: newItems };
    });
  };

  const handleRemoveItem = (index) => {
    setEditForm(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleAddProduct = (product) => {
    setEditForm(prev => {
      const existingIdx = prev.items.findIndex(i => i.product_id === product.id);
      if (existingIdx >= 0) {
        const newItems = [...prev.items];
        const newQty = newItems[existingIdx].quantity + 1;
        newItems[existingIdx] = {
          ...newItems[existingIdx],
          quantity: newQty,
          subtotal: newQty * newItems[existingIdx].unit_price
        };
        return { ...prev, items: newItems };
      }
      const price = product.your_price || product.wholesale_price;
      return {
        ...prev,
        items: [...prev.items, {
          product_id: product.id,
          product_name: product.name,
          pack_size: product.pack_size,
          quantity: 1,
          unit_price: price,
          subtotal: price,
          original_price: product.wholesale_price,
          discount_percent: product.discount_percent || 0,
          discount_amount: product.discount_amount || 0,
          has_discount: product.has_discount || false,
          image_url: product.image_url || null,
        }]
      };
    });
    setAddItemOpen(false);
    setProductSearch('');
  };

  const handleLocationChange = (locationId) => {
    const loc = profile?.locations?.find(l => l.id === locationId);
    setEditForm(prev => ({
      ...prev,
      location_id: locationId,
      delivery_address: loc ? `${loc.address}, ${loc.city}, ${loc.state} ${loc.zip_code}` : prev.delivery_address
    }));
  };

  const handleSaveOrder = async () => {
    if (!editForm.items || editForm.items.length === 0) {
      toast.error('Order must contain at least one item');
      return;
    }
    if (!editForm.delivery_date) {
      toast.error('Please select a delivery date');
      return;
    }

    setSaving(true);
    try {
      const updatePayload = {
        items: editForm.items.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name,
          pack_size: item.pack_size || 'dozen',
          quantity: item.quantity,
          unit_price: item.unit_price,
          subtotal: item.unit_price * item.quantity,
          original_price: item.original_price || null,
          discount_percent: item.discount_percent || 0,
          discount_amount: item.discount_amount || 0,
          has_discount: item.has_discount || false,
          image_url: item.image_url || null,
        })),
        delivery_date: editForm.delivery_date instanceof Date
          ? editForm.delivery_date.toISOString()
          : safeDate(editForm.delivery_date)?.toISOString(),
        delivery_time_window: editForm.delivery_time_window,
        location_id: editForm.location_id,
        delivery_address: editForm.delivery_address,
        po_number: editForm.po_number || null,
        order_notes: editForm.order_notes || null,
      };

      await clientPortalService.updateOrder(order.id, updatePayload);
      toast.success('Order updated successfully!');
      setEditMode(false);
      setEditForm(null);
      setLoading(true);
      await loadOrder();
    } catch (error) {
      console.error('Error saving order:', error);
      const msg = error.response?.data?.detail || 'Failed to update order';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!order) return;
    if (!window.confirm('Are you sure you want to cancel this order? This action cannot be undone.')) return;
    setCancelling(true);
    try {
      await clientPortalService.cancelOrder(order.id);
      toast.success('Order cancelled successfully');
      await loadOrder();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to cancel order';
      toast.error(errorMessage);
    } finally {
      setCancelling(false);
    }
  };

  const handleReorder = async () => {
    if (!order || !order.items || order.items.length === 0) {
      toast.error('No items in this order to reorder');
      return;
    }
    setReordering(true);
    try {
      clearCart();
      for (const item of order.items) {
        const cartItem = {
          id: item.product_id,
          name: item.product_name,
          wholesale_price: item.unit_price,
          pack_size: item.pack_size,
          image: item.image_url || '',
          quantity: item.quantity
        };
        for (let i = 0; i < item.quantity; i++) {
          addToCart(cartItem);
        }
      }
      toast.success('Items added to cart! Redirecting to checkout...');
      setTimeout(() => navigate('/client/checkout'), 500);
    } catch (error) {
      toast.error('Failed to reorder. Please try again.');
    } finally {
      setReordering(false);
    }
  };

  // Date picker helpers for edit mode
  const allowedDeliveryDays = profile?.account?.preferences?.allowed_delivery_days || [];
  const isDateDisabled = (date) => {
    const today = startOfDay(new Date());
    if (date < today) return true;
    const bizDays = countBusinessDays(today, startOfDay(date));
    if (bizDays < 2) return true;
    if (allowedDeliveryDays.length > 0) {
      const dayName = dayNameMap[date.getDay()];
      if (!allowedDeliveryDays.includes(dayName)) return true;
    }
    return false;
  };

  const editTotal = editForm
    ? editForm.items.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0)
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-amber-500"></div>
          <p className="mt-4 text-gray-600">Loading order...</p>
        </div>
      </div>
    );
  }

  if (!order) return null;

  const status = statusConfig[order.status] || statusConfig.submitted;
  const StatusIcon = status.icon;
  const editable = canEditOrder(order);
  const changeRequestable = canRequestChange(order);
  const isAdvanceInvoiced = order.billing_timing === 'advance' && !!order.invoice_id;
  const editDeadline = getEditDeadline(order.delivery_date);

  const timelineSteps = [
    { key: 'submitted', label: 'Submitted' },
    { key: 'confirmed', label: 'Confirmed' },
    { key: 'in_production', label: 'In Production' },
    { key: 'out_for_delivery', label: 'Out for Delivery' },
    { key: 'delivered', label: 'Delivered' },
  ];
  // A held order has been submitted and is waiting on the bakery — it sits at
  // the first step rather than nowhere on the line.
  const currentStepIndex = order.status === 'pending_approval'
    ? 0
    : timelineSteps.findIndex(s => s.key === order.status);

  // Filtered products for add item dialog
  const filteredProducts = products.filter(p =>
    p.status === 'available' &&
    p.name.toLowerCase().includes(productSearch.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="order-detail-page">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="icon" className="shrink-0" onClick={() => navigate('/client/orders')} data-testid="back-to-orders-btn">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="min-w-0 flex-1 basis-40">
          <h1 className="text-2xl font-bold text-gray-900 whitespace-nowrap">{order.order_number}</h1>
          <p className="text-sm text-gray-600">
            Placed on {new Date(order.created_at).toLocaleDateString('en-US', {
              weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
            })}
          </p>
        </div>
        {order.has_pending_change_request && (
          <Badge className="bg-indigo-100 text-indigo-700 text-sm py-1 px-3 shrink-0" data-testid="change-pending-chip">
            Change pending
          </Badge>
        )}
        <Badge className={`${status.color} text-sm py-1 px-3 shrink-0`}>
          <StatusIcon className="h-4 w-4 mr-1" />
          {status.label}
        </Badge>
      </div>

      {/* Over-ordering hold: what is held, by when, and how it was settled */}
      {order.over_cap && <OverCapNotice order={order} />}

      {/* Advance-invoice notice: direct edits are replaced by change requests */}
      {isAdvanceInvoiced && !editMode && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-slate-50 border border-slate-200 rounded-lg" data-testid="advance-invoice-notice">
          <Info className="h-4 w-4 text-slate-500 flex-shrink-0" />
          <p className="text-sm text-slate-700 flex-1">
            {order.invoice_number ? `Invoice ${order.invoice_number} ` : 'This order '}
            was issued in advance, so changes need bakery approval.
            {order.has_pending_change_request && ' Your change request is being reviewed.'}
          </p>
          {changeRequestable && (
            <Button
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 text-white min-h-[44px]"
              onClick={() => setChangeOpen(true)}
              data-testid="request-change-btn"
            >
              <Edit2 className="h-4 w-4 mr-1" />
              Request Change
            </Button>
          )}
        </div>
      )}

      {/* Editable deadline banner */}
      {editable && !editMode && editDeadline && (
        <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="edit-deadline-banner">
          <Info className="h-4 w-4 text-blue-600 flex-shrink-0" />
          <p className="text-sm text-blue-700">
            You can edit this order until{' '}
            <span className="font-semibold">
              {editDeadline.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
            </span>
            {' '}(2 business days before delivery).
          </p>
          <Button
            size="sm"
            className="ml-auto bg-blue-600 hover:bg-blue-700 text-white"
            onClick={handleStartEdit}
            data-testid="edit-order-btn"
          >
            <Edit2 className="h-4 w-4 mr-1" />
            Edit Order
          </Button>
        </div>
      )}

      {/* Edit mode header */}
      {editMode && (
        <div className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="edit-mode-banner">
          <p className="text-sm font-medium text-amber-800">Editing order — changes are not saved until you click Save.</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleCancelEdit} data-testid="cancel-edit-btn">
              <X className="h-4 w-4 mr-1" /> Cancel
            </Button>
            <Button
              size="sm"
              className="bg-amber-500 hover:bg-amber-600"
              onClick={handleSaveOrder}
              disabled={saving}
              data-testid="save-order-btn"
            >
              <Save className="h-4 w-4 mr-1" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      )}

      {/* Status Timeline */}
      {order.status !== 'cancelled' && !editMode && (
        <Card data-testid="order-timeline">
          <CardHeader><CardTitle className="text-lg">Order Status</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-center justify-between relative">
              <div className="absolute top-4 left-0 right-0 h-1 bg-gray-200">
                <div className="h-full bg-amber-500 transition-all"
                  style={{ width: `${(currentStepIndex / (timelineSteps.length - 1)) * 100}%` }}
                />
              </div>
              {timelineSteps.map((step, index) => {
                const isCompleted = index <= currentStepIndex;
                const isCurrent = index === currentStepIndex;
                return (
                  <div key={step.key} className="flex flex-col items-center relative z-10">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      isCompleted ? 'bg-amber-500 text-white' : 'bg-gray-200 text-gray-400'
                    } ${isCurrent ? 'ring-4 ring-amber-200' : ''}`}>
                      {isCompleted ? <CheckCircle className="h-4 w-4" /> : index + 1}
                    </div>
                    <p className={`mt-2 text-xs font-medium ${isCompleted ? 'text-amber-600' : 'text-gray-400'}`}>
                      {step.label}
                    </p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Proof of Delivery */}
      {!editMode && (order.delivery_photos?.length > 0 || order.production_photos?.length > 0 || order.pickup_summary?.length > 0) && (
        <Card data-testid="proof-of-delivery-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Camera className="h-5 w-5 text-green-600" />
              Proof of Delivery
            </CardTitle>
            {order.delivered_at ? (
              <p className="text-sm text-gray-500" data-testid="delivered-timestamp">
                Delivered {new Date(order.delivered_at).toLocaleString()}
              </p>
            ) : (
              <p className="text-sm text-gray-500">
                Photos taken while preparing and delivering this order. They stay on this page — come back any time.
              </p>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <PhotoProofGallery
              testIdPrefix="client-proof"
              emptyLabel="No photos on this order"
              groups={[
                {
                  key: 'production',
                  title: 'Packed in our kitchen',
                  caption: 'before it left the bakery',
                  photos: order.production_photos || [],
                },
                {
                  key: 'delivery',
                  title: 'At your door',
                  caption: 'taken on arrival',
                  photos: order.delivery_photos || [],
                },
              ]}
            />
            {order.pickup_summary?.length > 0 && (
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200" data-testid="pickup-summary-section">
                <p className="text-sm font-medium text-amber-800 mb-2">
                  Quantities were adjusted at pickup — your invoice reflects the delivered quantities.
                </p>
                <div className="space-y-1">
                  {order.pickup_summary.map((line) => (
                    <p key={line.product_name} className="text-sm text-amber-700">
                      {line.product_name}: Ordered {line.ordered_qty} → Delivered {line.delivered_qty}
                      {line.reason && <span className="text-amber-600"> ({line.reason})</span>}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Order Items */}
        <div className="lg:col-span-2">
          <Card data-testid="order-items-card">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-lg">Order Items</CardTitle>
                {editMode && (
                  <Button variant="outline" size="sm" onClick={() => setAddItemOpen(true)} data-testid="add-item-btn">
                    <Plus className="h-4 w-4 mr-1" /> Add Item
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {(editMode ? editForm.items : order.items).map((item, index) => (
                  <div key={item.product_id || `item-${index}`} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg" data-testid={`order-item-${index}`}>
                    {item.image_url && (
                      <img src={item.image_url} alt={item.product_name} className="w-12 h-12 object-cover rounded" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{item.product_name}</p>
                      <p className="text-sm text-gray-500">{item.pack_size}</p>
                    </div>
                    {editMode ? (
                      <div className="flex items-center gap-2">
                        <div className="flex items-center border rounded-md">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleUpdateQuantity(index, -1)} data-testid={`decrease-qty-${index}`}>
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="w-8 text-center text-sm font-medium" data-testid={`qty-${index}`}>{item.quantity}</span>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleUpdateQuantity(index, 1)} data-testid={`increase-qty-${index}`}>
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                        <p className="w-20 text-right font-semibold">${(item.unit_price * item.quantity).toFixed(2)}</p>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700" onClick={() => handleRemoveItem(index)} data-testid={`remove-item-${index}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="text-right">
                        {item.has_discount ? (
                          <>
                            <p className="text-xs text-gray-400">
                              {item.quantity} x <span className="line-through">${item.original_price?.toFixed(2)}</span>
                              {' '}<span className="text-green-600">${item.unit_price.toFixed(2)}</span>
                            </p>
                            <p className="font-semibold">${item.subtotal.toFixed(2)}</p>
                            <p className="text-xs text-green-600">
                              Save ${((item.discount_amount || 0) * item.quantity).toFixed(2)}
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="text-sm text-gray-600">{item.quantity} x ${item.unit_price.toFixed(2)}</p>
                            <p className="font-semibold">${item.subtotal.toFixed(2)}</p>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                {editMode && editForm.items.length === 0 && (
                  <div className="text-center py-6 text-gray-400">
                    <Package className="h-8 w-8 mx-auto mb-2" />
                    <p>No items yet. Add products to your order.</p>
                  </div>
                )}
              </div>
              <div className="border-t mt-4 pt-4 space-y-2">
                {!editMode && order.total_savings > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Your Savings</span>
                    <span>-${order.total_savings.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold">
                  <span>Total</span>
                  <span className="text-amber-600" data-testid="order-total">
                    ${editMode ? editTotal.toFixed(2) : order.total_amount.toFixed(2)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar: Delivery Info / Actions */}
        <div className="space-y-6">
          {/* Delivery Info */}
          <Card data-testid="delivery-info-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Truck className="h-5 w-5 text-amber-500" />
                Delivery
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {editMode ? (
                <>
                  {/* Delivery Date */}
                  <div>
                    <Label className="text-xs text-gray-500 uppercase">Delivery Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal mt-1" data-testid="edit-delivery-date-btn">
                          <Calendar className="mr-2 h-4 w-4" />
                          {editForm.delivery_date
                            ? format(editForm.delivery_date, 'EEE, MMM d, yyyy')
                            : 'Pick a date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={editForm.delivery_date}
                          onSelect={(date) => setEditForm(prev => ({ ...prev, delivery_date: date }))}
                          disabled={isDateDisabled}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Location */}
                  <div>
                    <Label className="text-xs text-gray-500 uppercase">Delivery Location</Label>
                    <Select value={editForm.location_id} onValueChange={handleLocationChange}>
                      <SelectTrigger className="mt-1" data-testid="edit-location-select">
                        <SelectValue placeholder="Select location" />
                      </SelectTrigger>
                      <SelectContent>
                        {profile?.locations?.map(loc => (
                          <SelectItem key={loc.id} value={loc.id}>
                            {loc.name} — {loc.address}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Time Window */}
                  <div>
                    <Label className="text-xs text-gray-500 uppercase">Time Window</Label>
                    <Select
                      value={editForm.delivery_time_window}
                      onValueChange={(v) => setEditForm(prev => ({ ...prev, delivery_time_window: v }))}
                    >
                      <SelectTrigger className="mt-1" data-testid="edit-time-window-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {timeWindows.map(tw => (
                          <SelectItem key={tw} value={tw}>{tw}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* PO Number */}
                  <div>
                    <Label className="text-xs text-gray-500 uppercase">PO Number</Label>
                    <Input
                      value={editForm.po_number}
                      onChange={(e) => setEditForm(prev => ({ ...prev, po_number: e.target.value }))}
                      className="mt-1"
                      placeholder="Optional"
                      data-testid="edit-po-number"
                    />
                  </div>

                  {/* Notes */}
                  <div>
                    <Label className="text-xs text-gray-500 uppercase">Order Notes</Label>
                    <Textarea
                      value={editForm.order_notes}
                      onChange={(e) => setEditForm(prev => ({ ...prev, order_notes: e.target.value }))}
                      className="mt-1"
                      rows={2}
                      placeholder="Special instructions..."
                      data-testid="edit-order-notes"
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-start gap-2">
                    <Calendar className="h-4 w-4 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">
                        {safeDate(order.delivery_date)?.toLocaleDateString('en-US', {
                          weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
                        })}
                      </p>
                      <p className="text-xs text-gray-500">{order.delivery_time_window}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                    <p className="text-sm text-gray-600">{order.delivery_address}</p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Additional Info (view mode only) */}
          {!editMode && (order.po_number || order.order_notes) && (
            <Card data-testid="additional-info-card">
              <CardHeader><CardTitle className="text-lg">Additional Info</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {order.po_number && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase">PO Number</p>
                    <p className="text-sm font-medium">{order.po_number}</p>
                  </div>
                )}
                {order.order_notes && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Notes</p>
                    <p className="text-sm text-gray-600">{order.order_notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Action Buttons (view mode only) */}
          {!editMode && (
            <div className="space-y-3">
              {editable && (
                <Button
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  data-testid="edit-order-btn-sidebar"
                  onClick={handleStartEdit}
                >
                  <Edit2 className="h-4 w-4 mr-2" /> Edit Order
                </Button>
              )}
              {changeRequestable && (
                <Button
                  className="w-full bg-blue-600 hover:bg-blue-700 min-h-[44px]"
                  data-testid="request-change-btn-sidebar"
                  onClick={() => setChangeOpen(true)}
                >
                  <Edit2 className="h-4 w-4 mr-2" /> Request Change
                </Button>
              )}
              <Button
                className="w-full bg-amber-500 hover:bg-amber-600"
                data-testid="reorder-btn"
                onClick={handleReorder}
                disabled={reordering}
              >
                {reordering ? (
                  <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Adding to Cart...</>
                ) : (
                  <><RefreshCw className="h-4 w-4 mr-2" /> Reorder This Order</>
                )}
              </Button>
              {order.status === 'submitted' && (
                <Button
                  variant="outline"
                  className="w-full border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                  data-testid="cancel-order-btn"
                  onClick={handleCancelOrder}
                  disabled={cancelling || isAdvanceInvoiced}
                  title={isAdvanceInvoiced ? 'Contact the bakery to cancel a prepaid order' : undefined}
                >
                  {cancelling ? (
                    <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Cancelling...</>
                  ) : (
                    <><XCircle className="h-4 w-4 mr-2" /> Cancel Order</>
                  )}
                </Button>
              )}
            </div>
          )}

          {/* Save/Cancel at bottom for edit mode */}
          {editMode && (
            <div className="space-y-3">
              <Button
                className="w-full bg-amber-500 hover:bg-amber-600"
                onClick={handleSaveOrder}
                disabled={saving || editForm.items.length === 0}
                data-testid="save-order-btn-bottom"
              >
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button variant="outline" className="w-full" onClick={handleCancelEdit}>
                Cancel Editing
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Add Item Dialog */}
      <Dialog open={addItemOpen} onOpenChange={setAddItemOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Product</DialogTitle>
            <DialogDescription className="sr-only">Add Product dialog. Use the form below to continue.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search products..."
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                className="pl-10"
                data-testid="add-item-search"
              />
            </div>
            <div className="space-y-2 max-h-[50vh] overflow-y-auto">
              {filteredProducts.length === 0 ? (
                <p className="text-center py-6 text-gray-400">No products found</p>
              ) : (
                filteredProducts.map(product => (
                  <div
                    key={product.id}
                    className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer border"
                    onClick={() => handleAddProduct(product)}
                    data-testid={`add-product-${product.id}`}
                  >
                    {product.image_url && (
                      <img src={product.image_url} alt={product.name} className="w-10 h-10 object-cover rounded" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{product.name}</p>
                      <p className="text-xs text-gray-500">{product.pack_size}</p>
                    </div>
                    <div className="text-right">
                      {product.has_discount ? (
                        <>
                          <p className="text-sm font-semibold text-green-600">${product.your_price.toFixed(2)}</p>
                          <p className="text-xs text-gray-400 line-through">${product.wholesale_price.toFixed(2)}</p>
                        </>
                      ) : (
                        <p className="text-sm font-semibold">${product.wholesale_price.toFixed(2)}</p>
                      )}
                    </div>
                    <Plus className="h-5 w-5 text-amber-500" />
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <RequestChangeDialog
        open={changeOpen}
        onOpenChange={setChangeOpen}
        order={order}
        onSubmitted={loadOrder}
      />
    </div>
  );
};

const Orders = () => {
  const { id } = useParams();
  
  if (id) {
    return <OrderDetail />;
  }
  
  return <OrdersList />;
};

export default Orders;
